import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useFeatureFlags } from "../queries/featureFlags";
import { FEATURE_FLAGS } from "../types/featureFlags.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
const NOTES_STALE_TIME = STALE_TIME_MEDIUM;
/**
 * Whether note queries should be limited to manual associations.
 * Spatially-computed (non-manual) associations are only shown to customers
 * with the NoteIntersections feature flag.
 */
export function useNoteManualOnly() {
    const { featureFlags } = useFeatureFlags();
    return computed(() => !(featureFlags.value?.includes(FEATURE_FLAGS.NoteIntersections) ??
        false));
}
export function useGetNotePhotoUploadUrls() {
    const isAuthorized = useAuthorizedQuery();
    return useMutation({
        mutationFn: async (photos) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const photoRequests = photos.map((photo) => ({
                fileName: photo.name,
                mimeType: photo.mimeType,
            }));
            const response = await seedgreenAPI.post("v2/note/UploadUrls", photoRequests);
            return response.data;
        },
    });
}
export function useCreateNote() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (noteData) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/note", noteData);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["notes"],
            });
        },
    });
    return {
        createNote: mutateAsync,
        isLoading: isPending,
        error,
    };
}
export function useUpdateNote() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async ({ noteId, noteData, }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.put(`v2/note/${noteId}`, noteData);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["notes"],
            });
        },
    });
    return {
        updateNote: mutateAsync,
        isLoading: isPending,
        error,
    };
}
export function useAddPhotosToNote() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async ({ noteId, photos, }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post(`v2/note/${noteId}/photos`, photos);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notes"] });
        },
    });
    return {
        addPhotosToNote: mutateAsync,
        isLoading: isPending,
        error,
    };
}
export function useEntityNotes(entityType, entityId, manualOnly = ref(false), includeInactive = ref(false)) {
    const isAuthorized = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => isAuthorized.value && !!entityType.value && !!entityId.value),
        queryKey: ["notes", entityType, entityId, manualOnly, includeInactive],
        queryFn: async () => {
            const response = await seedgreenAPI.get(`v2/note`, {
                params: {
                    entityType: entityType.value,
                    entityId: entityId.value,
                    manualOnly: manualOnly.value,
                    includeInactive: includeInactive.value,
                },
            });
            return response.data;
        },
        staleTime: NOTES_STALE_TIME,
        select: (data) => {
            try {
                return data;
            }
            catch (error) {
                console.error(`query/note::useEntityNotes ERROR for ${entityType.value}/${entityId.value}`, error);
                return [];
            }
        },
    });
    return {
        notes: query.data,
        ...query,
    };
}
export function useActionRequiredEntityIds(entityType, entityIds, manualOnly = ref(true)) {
    const isAuthorized = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => isAuthorized.value && entityIds.value.length > 0),
        queryKey: computed(() => {
            // Create a stable, normalized key: deduplicated and sorted
            const normalizedIds = [...new Set(entityIds.value)].sort((a, b) => a - b);
            return [
                "notes",
                "action-required",
                entityType.value,
                manualOnly.value,
                normalizedIds,
            ];
        }),
        queryFn: async () => {
            const normalizedIds = [...new Set(entityIds.value)].sort((a, b) => a - b);
            const response = await seedgreenAPI.post("v2/note/action-required/query", {
                entityType: entityType.value,
                entityIds: normalizedIds,
                manualOnly: manualOnly.value,
            });
            return response.data;
        },
        staleTime: NOTES_STALE_TIME,
        select: (data) => data ?? [],
    });
    return {
        actionRequiredEntityIds: query.data,
        ...query,
    };
}
export function useDeleteNote() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (noteId) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.delete(`v2/note/${noteId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notes"] });
        },
    });
    return {
        deleteNote: mutateAsync,
        isLoading: isPending,
        error,
    };
}
